import React, { useContext, useEffect, useState } from "react"
import { Page, PageHeader } from "@components"
import { NotificationContext, UserContext } from "@context"

import { 
  DELETE_ASSET_MUTATION, 
  GET_CLIENT_SETTINGS, 
  UPDATE_CLIENT_SETTINGS_MUTATION, 
  UPLOAD_ASSET 
} from "@graphql"

import { getGraphQLErrorMessage, getValidationErrors } from "@helpers"
import { useSetActiveSidebarLink } from "@hooks"
import { useMutation, useQuery } from "@apollo/client"
import { Cog6ToothIcon } from "@heroicons/react/24/outline"
import { camelCase } from "lodash"
import Bookings from "./Bookings"
import ContactDetails from "./ContactDetails"
import Images from "./Images"
import GuardianInvitations from "./GuardianInvitations"
import ParentPermissions from "./ParentPermissions"
import TeacherPermissions from "./TeacherPermissions"
import Theme from "./Theme"

const SiteSettings = () => {
  const { notify } = useContext(NotificationContext)
  const { user } = useContext(UserContext)
  useSetActiveSidebarLink("SITE_SETTINGS")

  const [isSaving, setIsSaving] = useState({
    bookings: false,
    contactDetails: false,
    images: false,
    guardianInvitations: false,
    parentPermissions: false,
    teacherPermissions: false,
    theme: false
  })
  
  const [assets, setAssets] = useState(null)
  const [currentAssetType, setCurrentAssetType] = useState(null)
  const [isAssetDeleting, setIsAssetDeleting] = useState(false)
  const [isImageUploadAreaVisible, setIsImageUploadAreaVisible] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [lastSavedSection, setLastSavedSection] = useState(null);
  const [selectedFileForUpload, setSelectedFileForUpload] = useState(null)

  const [form, setForm] = useState({
    bookings: {
      unbook_on_non_attendance: false, 
    },

    contactDetails: {
      email: "",
      phone: "",
    },

    theme: {
      logo_background_color: "",
    },

    guardianInvitations: {
      require_legal_guardian: false,
    },
  
    parentPermissions: {
      allow_parent_notes: false,
      allow_parent_waiting_list: false,
    },

    teacherPermissions: {
      allow_teacher_availability: false,
      allow_teacher_bookings: false,
      allow_teacher_reports: false,
    },
  })
 
  const { data, loading } = useQuery(GET_CLIENT_SETTINGS, {
    variables: {
      clientId: user?.me?.client?.id,
    },
    fetchPolicy: "cache-first",
    onError: (error) => {
      const errorMessage = getValidationErrors(error, true) || "An undefined error occurred while fetching the client settings"
      notify({ title: "Error", type: "danger", content: errorMessage, open: true })
    }
  })
 
  const [deleteAsset] = useMutation(DELETE_ASSET_MUTATION, {
    onError: errors => {
      const errorMessage = getValidationErrors(errors, true) || "An undefined error occurred while deleting the asset"
      notify({ title: "Error", type: "danger", content: errorMessage, open: true })
      setIsAssetDeleting(false)
    },

    onCompleted: (data) => {
      const key = camelCase(data?.deleteAsset?.type?.type)

      if (key) {
        setAssets(prev => {
          const { [key]: _, ...rest } = prev
          return rest
        })
      }
    }
  })

  const [updateSettings] = useMutation(UPDATE_CLIENT_SETTINGS_MUTATION, {
    onCompleted: () => {
      notify({ title: "Success", type: "success", content: "Settings updated", open: true })
    },

    onError: errors => {
      notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while updating setting"), open: true })
    }
  })

  const [uploadAsset] = useMutation(UPLOAD_ASSET, {
    onCompleted: (data) => {
      const key = camelCase(data?.uploadAsset?.type?.type)

      setAssets(prev => ({
        ...prev,
        [key]: data?.uploadAsset,
      }))

      setSelectedFileForUpload(null)
      setIsImageUploadAreaVisible(false)
      setCurrentAssetType(null)
    },

    onError: errors => {
      const errorMessage = getValidationErrors(errors, true) || "An undefined error occurred while uploading the asset"
      setSelectedFileForUpload(null)
      setIsImageUploadAreaVisible(false)
      setCurrentAssetType(null)
      notify({ title: "Error", type: "danger", content: errorMessage, open: true })
    }
  })

  useEffect(() => {
    if (data?.client?.settings) {
      if (!lastSavedSection) {
        setAssets(data?.client?.assets?.reduce((assets, asset) => {
          assets[camelCase(asset.type.type)] = asset
          return assets
        }, {}))
      }
     
      const newForm = {
        bookings: {
          unbook_on_non_attendance: data?.client?.settings?.unbook_on_non_attendance || false,
        },

        contactDetails: {
          email: data?.client?.settings?.email || "",
          phone: data?.client?.settings?.phone || "",
        },
                
        guardianInvitations: {
          require_legal_guardian: data?.client?.settings?.require_legal_guardian || false,
        },
        
        parentPermissions: {
          allow_parent_notes: data?.client?.settings?.allow_parent_notes || false,
          allow_parent_waiting_list: data?.client?.settings?.allow_parent_waiting_list || false,
        },

        teacherPermissions: {
          allow_teacher_bookings: data?.client?.settings?.allow_teacher_bookings || false,
          allow_teacher_availability: data?.client?.settings?.allow_teacher_availability || false,
          allow_teacher_reports: data?.client?.settings?.allow_teacher_reports || false,
        },

        theme: {
          logo_background_color: data?.client?.settings?.logo_background_color || "#FFFFFF",
        },
      }

      if (!lastSavedSection) {
        setForm(newForm)
      } else {
        setForm(prevForm => ({...prevForm, [lastSavedSection]: newForm[lastSavedSection] }))
        setLastSavedSection(null); // Reset tracker
      }  
    }
  }, [data?.client?.settings]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancelUpload = () => {
    setSelectedFileForUpload(null)
    setIsImageUploadAreaVisible(false)
    setCurrentAssetType(null)
  }

  const handleDeleteAsset = (asset, code) => {
    setCurrentAssetType(code)
    setIsAssetDeleting(true)

    deleteAsset({
      variables: {
        input: {
          id: asset.id,
        }
      }
    }).finally(() => {
      setIsAssetDeleting(false)
      setCurrentAssetType(null)
    })
  }

  const handleSaveSettings = (section) => {
    setIsSaving(prev => ({ ...prev, [section]: true }))
    setLastSavedSection(section)

    updateSettings({
      variables: {
        client_id: data.client.id,
        input: {...form[section]},
      },
    }).finally(() => {
      setIsSaving(prev => ({ ...prev, [section]: false }))
    })
  }

  const handleShowImageUploadArea = (code, isVisible) => {
    setCurrentAssetType(code)
    setIsImageUploadAreaVisible(isVisible)
  }

  const handleUpdateSetting = (section, setting, value) => {
    setForm(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [setting]: value,
      },
    }))
  }

  const handleUploadAsset = (code) => {
    setIsUploading(true)

    uploadAsset({
      variables: {
        input: {
          client_id: user?.me?.client?.id,
          code,
          file: selectedFileForUpload,
        }
      },
    }).finally(() => setIsUploading(false))
  }

  const isBookingsFormDirty = () => {
    return form?.bookings?.unbook_on_non_attendance !== data?.client?.settings.unbook_on_non_attendance
  }
  
  const isContactFormDirty = () => {
    return (
      form?.contactDetails?.email !== data?.client?.settings.email ||
      form?.contactDetails?.phone !== data?.client?.settings.phone
    )
  }

  const isGuardianInvitationsFormDirty = () => {
    return (
      form?.guardianInvitations?.require_legal_guardian !== data?.client?.settings.require_legal_guardian
    )
  }

  const isParentPermissionFormDirty = () => {
    return (
      form?.parentPermissions?.allow_parent_notes !== data?.client?.settings.allow_parent_notes || 
      form?.parentPermissions?.allow_parent_waiting_list !== data?.client?.settings.allow_parent_waiting_list
    )
  }

  const isTeacherPermissionFormDirty = () => {
    return (
      form?.teacherPermissions?.allow_teacher_availability !== data?.client?.settings.allow_teacher_availability ||
      form?.teacherPermissions?.allow_teacher_bookings !== data?.client?.settings.allow_teacher_bookings ||
      form?.teacherPermissions?.allow_teacher_reports !== data?.client?.settings.allow_teacher_reports
    )
  }

  const isThemesFormDirty = () => {
    return form?.theme?.logo_background_color !== data?.client?.settings.logo_background_color
  }

  return (
    <Page>
      <PageHeader
        title="Site Settings"
        subTitle="Site Level Settings"
        icon={<Cog6ToothIcon className="page-header-icon" />}
      />

      <Images
        currentAssetType={currentAssetType}
        currentSelectedFile={selectedFileForUpload}
        handleCancelUpload={handleCancelUpload}
        handleDeleteAsset={(asset, code) => handleDeleteAsset(asset, code)}
        handleFileSelected={(file) => setSelectedFileForUpload(file)}
        handleShowImageUploadArea={(code) => handleShowImageUploadArea(code, true)}
        handleUploadAsset={(code) => handleUploadAsset(code)}
        isAssetDeleting={isAssetDeleting}
        isImageUploadAreaVisible={isImageUploadAreaVisible}
        isLoading={loading}
        isUploading={isUploading}
        logo={assets?.logo}
        schoolBackgroundImage={assets?.loginParentBackground}
        uploadPreviewImage={selectedFileForUpload}
      />

      <Theme
        handleSave={() => handleSaveSettings("theme")}
        handleUpdate={(key, setting) => handleUpdateSetting("theme", key, setting)}
        isDirty={isThemesFormDirty()}
        isLoading={loading}
        isSaving={isSaving.theme}
        logoBackground={form?.theme?.logo_background_color || ""}
      />

      <ContactDetails 
        email={form?.contactDetails?.email || ""} 
        handleSave={() => handleSaveSettings("contactDetails")}
        handleUpdate={(key, setting) => handleUpdateSetting("contactDetails", key, setting)}
        isDirty={isContactFormDirty()}
        isLoading={loading}
        isSaving={isSaving.contactDetails}
        phone={form?.contactDetails?.phone || ""} 
      />
      
      <TeacherPermissions 
        handleSave={() => handleSaveSettings("teacherPermissions")}
        handleUpdate={(key, setting) => handleUpdateSetting("teacherPermissions", key, setting)}
        isDirty={isTeacherPermissionFormDirty()}
        isLoading={loading}
        isSaving={isSaving.teacherPermissions}
        settings={form?.teacherPermissions} 
      />

      <ParentPermissions 
        handleSave={() => handleSaveSettings("parentPermissions")}
        handleUpdate={(key, setting) => handleUpdateSetting("parentPermissions", key, setting)}
        isDirty={isParentPermissionFormDirty()}
        isLoading={loading}
        isSaving={isSaving.parentPermissions}
        settings={form?.parentPermissions} 
      />

      <Bookings 
        handleSave={() => handleSaveSettings("bookings")}
        handleUpdate={(key, setting) => handleUpdateSetting("bookings", key, setting)}
        isDirty={isBookingsFormDirty()}
        isLoading={loading}
        isSaving={isSaving.bookings}
        unbookOnNonAttendance={form.bookings.unbook_on_non_attendance} 
      />
      
      <GuardianInvitations 
        handleSave={() => handleSaveSettings("guardianInvitations")}
        handleUpdate={(key, setting) => handleUpdateSetting("guardianInvitations", key, setting)}
        isDirty={isGuardianInvitationsFormDirty()}
        isLoading={loading}
        isSaving={isSaving.guardianInvitations}
        settings={form.guardianInvitations} 
      />
    </Page>
  )
}

export default SiteSettings
